import { PlayingFile } from '../store/reducers/calls/reducer';

/**
 * Identity of a file inside the playback queue.
 *
 * It used to be the (call_recording_id, i_xdr) pair, which forced consumers that
 * play something other than a call recording - menu prompts, menu transitions,
 * on-hold music, ring group prompts, voicemails - to invent an i_xdr they do not
 * have. Files may now carry an explicit playbackKey instead; the fallback keeps
 * the previous behaviour for the ones that do not.
 *
 * Every builder below owns a distinct prefix, so a key minted by one domain can
 * never collide with a key minted by another - a plain `${id}:${n}` shape would
 * let a recording called 'voicemail' collide with voicemail message 'n'.
 */
export const cdrPlaybackKey = (
    call_recording_id: string,
    i_xdr?: number,
): string => `cdr:${call_recording_id}:${i_xdr}`;

export const playbackIdentityOf = (
    file?: PlayingFile | null,
): string | undefined =>
    file
        ? file.playbackKey ??
          cdrPlaybackKey(file.call_recording_id, file.i_xdr)
        : undefined;

export const menuPromptPlaybackKey = (
    i_menu: number,
    promptSlot: number,
): string => `menu:${i_menu}:${promptSlot}`;

export const menuTransitionPlaybackKey = (
    i_menu_transition: number,
): string => `menu-transition:${i_menu_transition}`;

export const voicemailPlaybackKey = (
    message_uid: number | string,
): string => `voicemail:${message_uid}`;

/**
 * Prompts that exist once per form (on-hold music, ring group intro, call queue
 * prompts, voicemail greetings). Pass an id only where the queue entry stands for
 * one of several interchangeable files - the form buttons have no id to give.
 */
export const promptPlaybackKey = (
    kind: string,
    id?: number | string,
): string => (id === undefined ? `prompt:${kind}` : `prompt:${kind}:${id}`);

export const isSamePlaybackFile = (
    file?: PlayingFile | null,
    other?: PlayingFile | null,
): boolean => {
    const key = playbackIdentityOf(file);
    return !!key && key === playbackIdentityOf(other);
};